import { tokenRefreshManager } from "./tokenRefresh";
const AWS = require("aws-sdk");

const Sockets = (apiData, user) => {
  const initializeCredentials = async () => {
    // Use pool configuration from apiData
    const userPoolId = apiData?.api?.userPool;
    const identityPoolId = apiData?.api?.identityPool;

    const credentials = new AWS.CognitoIdentityCredentials({
      IdentityPoolId: identityPoolId,
      Logins: {
        [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${userPoolId}`]: user?.idToken,
      },
    });

    AWS.config.update({
      region: process.env.REACT_APP_AWS_REGION,
      credentials: credentials,
    });

    // Get fresh credentials
    await credentials.getPromise();
    return credentials;
  };

  const readData = async (s3Link, bucket = "socket") => {
    let bucketName = apiData?.websocket?.socketBucket;
    if (bucket === "eod") {
      bucketName = apiData?.api?.eodBucket;
    }

    const performChunkedDownload = async (retryCount = 0) => {
      try {
        // Initialize fresh credentials for each operation
        await initializeCredentials();

        const s3 = new AWS.S3({
          useAccelerateEndpoint: true
        });

        let headParams = {
          Bucket: bucketName,
          Key: s3Link,
        };

        // Get the file size
        let headData = await s3.headObject(headParams).promise();
        const fileSize = headData.ContentLength;
        const chunkSize = 1024 * 1024;
        const numChunks = Math.ceil(fileSize / chunkSize);

        // Create an array of promises for each chunk
        const chunkPromises = Array.from({ length: numChunks }, (_, index) => {
          const rangeStart = index * chunkSize;
          const rangeEnd = Math.min(rangeStart + chunkSize - 1, fileSize - 1);

          let params = {
            Bucket: bucketName,
            Key: s3Link,
            Range: `bytes=${rangeStart}-${rangeEnd}`,
          };

          return s3.getObject(params).promise().then(data => data.Body.toString("utf-8"));
        });

        // Wait for all chunks to be fetched
        const chunks = await Promise.all(chunkPromises);
        const fileContent = chunks.join('');
        return fileContent;
      } catch (err) {
        console.error(`Error reading data from S3 (attempt ${retryCount + 1}):`, err);
        
        // Check for various token-related errors
        const isTokenError = err.message && (
          err.message.includes("Invalid login token") ||
          err.message.includes("The provided token has expired") ||
          err.message.includes("Token is not from a supported provider") ||
          err.message.includes("UnauthorizedOperation") ||
          err.message.includes("AccessDenied") ||
          err.code === 'CredentialsError' ||
          err.code === 'TokenRefreshRequiredError'
        );
        
        // If it's a token expiration error and we haven't retried yet, try once more
        if (isTokenError && retryCount === 0) {
          try {
            // Refresh JWT tokens using the refreshToken
            const refreshedUser = await tokenRefreshManager.refreshUserToken(user);

            // Update the user object for subsequent operations
            user = refreshedUser;

            // Wait a moment before retry to avoid race conditions
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Retry the download with refreshed tokens
            return await performChunkedDownload(1); // Retry with incremented count
          } catch (retryErr) {
            console.error("Retry failed after JWT token refresh attempt:", retryErr);
            // Throw a more descriptive error
            throw new Error(`S3 download failed after token refresh attempt: ${retryErr.message}. Your session may have expired - please try refreshing the page or logging out and back in.`);
          }
        }
        
        // If it's not a token error or we already retried, throw the original error
        if (isTokenError) {
          throw new Error(`S3 authentication failed: ${err.message}. Please try logging out and logging back in.`);
        } else {
          throw new Error(`S3 download failed: ${err.message}`);
        }
      }
    };

    return await performChunkedDownload();
  };

  return { readData };
};

export default Sockets;
